import { DISABLED_ANIMATION_NAME } from './components/MenuContent/MenuItem/useAnimationState';

export const MenuOrientation = {
  Horizontal: 'horizontal',
  Vertical: 'vertical',
} as const;
export type MenuOrientationType =
  (typeof MenuOrientation)[keyof typeof MenuOrientation];

export const VerticalDropdownDisplay = {
  AlwaysOpen: 'alwaysOpen',
  ExpandCollapse: 'expandCollapse',
} as const;
export type MenuVerticalDropdownDisplayType =
  (typeof VerticalDropdownDisplay)[keyof typeof VerticalDropdownDisplay];

export const DropdownAnchor = {
  menuItem: 'menuItem',
  menuStretched: 'menuStretched',
  menuCustomWidth: 'menuCustomWidth',
  screen: 'screen',
} as const;
export type DropdownAnchorType =
  (typeof DropdownAnchor)[keyof typeof DropdownAnchor];

export const MenuAnimationName = {
  None: DISABLED_ANIMATION_NAME,
  Calm: 'calm',
  Underline: 'underline',
  Wash: 'wash',
  Bullet: 'bullet',
  Wave: 'wave',
  Bounce: 'bounce',
  Tint: 'tint',
  TextWash: 'textWash',
  Wobble: 'wobble',
  Brackets: 'brackets',
  Float: 'float',
  Shape: 'shape',
  Expand: 'expand',
  Circle: 'circle',
  Shadow: 'shadow',
  LineRise: 'lineRise',
  Overline: 'overline',
  CenterLine: 'centerLine',
  Skew: 'skew',
  Point: 'point',
  Blur: 'blur',
} as const;
export type MenuAnimationNameType =
  (typeof MenuAnimationName)[keyof typeof MenuAnimationName];

export const DropdownAnimationName = {
  None: DISABLED_ANIMATION_NAME,
  RevealFromTop: 'revealFromTop',
  FadeIn: 'fadeIn',
} as const;
export type DropdownAnimationNameType =
  (typeof DropdownAnimationName)[keyof typeof DropdownAnimationName];

export const HamburgerMenuAnimationName = {
  None: DISABLED_ANIMATION_NAME,
  RevealFromRight: 'revealFromRight',
  FadeIn: 'fadeIn',
} as const;
export type HamburgerMenuAnimationNameType =
  (typeof HamburgerMenuAnimationName)[keyof typeof HamburgerMenuAnimationName];

export const DisplayNames = {
  root: {
    elementDisplayName: 'Menu',
    actions: {
      manageMenu: 'Manage Menu',
      design: 'Design',
      settings: 'Settings',
    },
    customActions: {
      format: {
        displayName: 'Change Format',
        panel: {
          menu: 'Menu',
          orientation: 'Orientation',
          overflowItems: {
            displayName: 'Item behavior',
            infoContent:
              'Choose whether visitors scroll to view menu items or see them all displayed at once',
          },
          menuTypeOptions: {
            navbar: 'Menu bar',
            hamburger: 'Hamburger menu',
          },
          orientationOptions: {
            horizontal: 'Horizontal',
            vertical: 'Vertical',
          },
          overflowOptions: {
            scroll: 'Fit to scroll',
            expandMenu: 'Fit to text',
          },
        },
      },
      animatedIcon: {
        displayName: 'Customize Icon',
      },
    },
    presets: {
      horizontalScrollNavbar: 'Horizontal Scroll Navigation Bar',
      horizontalHugNavbar: 'Horizontal Hug Navigation Bar',
      verticalNavbar: 'Vertical Navigation Bar',
      hamburgerMenu: 'Hamburger menu',
    },
    states: {
      hamburgerDialog: 'Hamburger menu',
      dropdown: 'Dropdown',
    },
  },
  navbar: {
    elementDisplayName: 'Menu bar',
    displayGroups: {
      dividerBorderGroup: 'Dividers',
    },
    cssCustomProperties: {
      'vertical-dropdown-display': 'Items display',
      'items-width': 'Item width',
      'container-align': 'Item alignment',
      'menu-items-row-gap': 'Space between menu items',
      'menu-items-column-gap': 'Space between menu items',
    },
    actions: {
      manageMenu: 'Manage Menu',
    },
    options: {
      alwaysOpen: 'Open',
      expandCollapse: 'Closed',
      fitToText: 'Fit to text',
      fillMenu: 'Fill menu',
      start: 'Start',
      end: 'End',
      top: 'Top',
      bottom: 'Bottom',
    },
  },
  item: {
    elementDisplayName: 'Menu items',
    cssProperties: {
      justifyContent: 'Text alignment',
      background: 'Background',
      columnGap: 'Space between menu items and icons',
      'item-icon-size': 'Size',
    },
    displayGroups: {
      iconGroup: 'Icons',
    },
    states: {
      hover: 'Hover',
      selected: 'Selected',
    },
  },
  dropdown: {
    elementDisplayName: 'Dropdowns',
    cssCustomProperties: {
      anchor: 'Dropdown width',
      align: 'Dropdown position',
      'items-width': 'Item width',
      'columns-number': 'Number of columns',
      'space-above': 'Space above dropdowns',
      'horizontal-margin': 'Dropdown margin',
      'item-position': 'Dropdown box position',
    },
    options: {
      menuItem: 'Fit to items',
      menuStretched: 'Fit to menu',
      screen: 'Stretch to page',
      start: 'Start',
      center: 'Middle',
      end: 'End',
      fitToText: 'Fit to text',
      fillDropdown: 'Fill dropdowns',
      afterItems: 'After items',
      beforeItems: 'Before items',
      aboveItems: 'Above items',
      belowItems: 'Below items',
    },
    displayGroups: {
      gapGroup: 'Space between items',
    },
  },
  dropdownItem: {
    elementDisplayName: 'Dropdown items',
  },
  dropdownSubItem: {
    elementDisplayName: 'Dropdown sub-items',
  },
  scrollButton: {
    elementDisplayName: 'Scroll button',
    cssProperties: {
      background: 'Background',
    },
    cssCustomProperties: {
      'scroll-button-icon-size': 'Size',
    },
    displayGroups: {
      scrollButtonGroup: 'Icon',
    },
  },
  hamburgerMenu: {
    elementDisplayName: 'Hamburger menu',
    cssCustomProperties: {
      'hamburger-wrapper-width': 'Width',
      'wrapper-position': 'Position',
      'menu-position': 'Menu Position',
      'close-button-position': 'Close button position',
      'overlay-background': 'Overlay Background',
    },
    options: {
      third: 'Third of page',
      twoThirds: 'Two thirds of page',
      half: 'Half page',
      full: 'Full page',
      start: 'Start',
      middle: 'Middle',
      end: 'End',
      top: 'Top',
      bottom: 'Bottom',
    },
  },
  hamburgerOpenButton: {
    elementDisplayName: 'Hamburger button',
  },
  hamburgerCloseButton: {
    elementDisplayName: 'Close Button',
  },
};

export const selectors = {
  menu: 'menu',
  navbar: 'navbar',
  item: 'item',
  dropdown: 'dropdown',
  dropdownItem: 'dropdown-item',
  dropdownSubItem: 'dropdown-sub-item',
  scrollButton: 'scroll-button',
  hamburgerMenu: 'hamburger-menu',
  hamburgerMenuContent: 'hamburger-menu-content',
  hamburgerMenuHeaderContent: 'hamburger-menu-header-content',
  hamburgerMenuOpenButton: 'hamburger-open-button',
  hamburgerMenuCloseButton: 'hamburger-close-button',
};

export const elementKeys = {
  navbar: 'navbar',
  hamburgerMenu: 'hamburgerMenu',
  hamburgerOpenButton: 'hamburgerOpenButton',
  hamburgerCloseButton: 'hamburgerCloseButton',
  item: 'item',
  subItem: 'subItem',
  scrollButton: 'scrollButton',
  dropdown: 'dropdown',
};

export const presets = {
  horizontalScrollNavbar: 'horizontalScrollNavbar',
  horizontalHugNavbar: 'horizontalHugNavbar',
  verticalNavbar: 'verticalNavbar',
  hamburgerMenu: 'hamburgerMenu',
};

export const states = {
  dropdown: 'dropdown',
  hamburgerDialog: 'hamburgerDialog',
  hamburgerDialogDropdown: 'hamburgerDialogDropdown',
};

export const DropdownAppliedStyleProperties = {
  anchor: '--wix-menu-dropdown-anchor',
};

export const DropdownAnchorPositions = {
  menuItem: '0',
  menuStretched: '1',
  screen: '2',
} as const;

export const ARIA_LABEL_NAMESPACE = 'ariaLabels';

export const translationsKeys = {
  menuNavAriaLabel: 'dropDownMenu_AriaLabel_TopLevel_SiteNavigation',
  dropdownButtonAriaLabel: 'menu_dropdown_chevron_button_aria_label',
};

export type Translations = {
  translations: {
    dropdownButtonAriaLabel?: string;
    menuNavAriaLabel?: string;
  };
};

export const defaultTranslations: Translations['translations'] = {
  menuNavAriaLabel: 'Site',
  dropdownButtonAriaLabel: 'More pages',
};

export const MenuParts = {
  Root: 'menu-root',
  Navbar: 'navbar',
  DropdownContainer: 'dropdown-container',
  DropdownButton: 'dropdown-button',
  DropdownIcon: 'dropdown-icon',
  Label: 'label',
  DropdownItem: 'dropdown-item',
  DropdownItemLabel: 'dropdown-item-label',
  DropdownSubItem: 'dropdown-subitem',
  DropdownSubItemLabel: 'dropdown-subitem-label',
  MenuItem: 'menu-item',
  MenuItemContent: 'menu-item-content',
  MenuItemLink: 'menu-item-link',
  HamburgerMenuContainer: 'hamburger-menu-container',
  HamburgerOverlay: 'hamburger-overlay',
  ScrollBackwardButton: 'scroll-backward-button',
  ScrollForwardButton: 'scroll-forward-button',
} as const;
